import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, ArrowRight, CheckCircle, Clock, AlertTriangle, Shield, Car
} from 'lucide-react';
import { companyInfo } from '../../data/mock';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import SEO from '../seo/SEO';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb';

const BrisDeGlacePage = () => {
  const pageTitle = "Bris de Glace Villiers-sur-Marne | Urgence 24h/24";
  const pageDescription = "Bris de glace à Villiers-sur-Marne ☎ 07 81 55 90 50. Intervention vitrier urgence 24h/24 pour bris de glace fenêtre, baie vitrée, vitrine. Sécurisation immédiate et remplacement.";
  const pageKeywords = "bris de glace Villiers-sur-Marne, bris de glace urgence, vitrier bris de glace, réparation bris de glace, bris de glace 94350, sécurisation vitre cassée";

  return (
    <div className="min-h-screen">
      <SEO 
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonical="/bris-de-glace-villiers-sur-marne"
      />

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-[hsl(215,25%,15%)] to-[hsl(215,25%,25%)]">
        <div className="container mx-auto px-4">
          <Breadcrumb className="mb-6">
            <BreadcrumbList className="text-white/70">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="hover:text-white">Accueil</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/vitrier-villiers-sur-marne" className="hover:text-white">Vitrier Villiers-sur-Marne</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-white">Bris de glace</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="max-w-4xl">
            <Badge className="bg-[hsl(24,95%,53%)] text-white mb-6">
              <AlertTriangle className="w-3 h-3 mr-1" /> Urgence 24h/24
            </Badge>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Bris de Glace <span className="text-[hsl(24,95%,53%)]">Villiers-sur-Marne</span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8">
              Intervention vitrier urgence pour <strong>bris de glace</strong> à Villiers-sur-Marne. 
              Sécurisation immédiate et remplacement de votre vitrage 24h/24.
            </p>

            <div className="flex flex-wrap gap-4 mb-8">
              <a href={`tel:${companyInfo.phoneEmergency}`}>
                <Button size="lg" className="emergency-btn rounded-full text-lg px-8 py-6">
                  <Phone className="w-5 h-5 mr-2" />
                  Urgence : {companyInfo.phoneEmergency}
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[hsl(207,78%,35%)] hover:bg-gray-100 rounded-full text-lg px-8 py-6">
                  Devis gratuit
                </Button>
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[hsl(24,95%,53%)]" />
                <span>Sécurisation immédiate</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[hsl(24,95%,53%)]" />
                <span>24h/24 - 7j/7</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[hsl(24,95%,53%)]" />
                <span>Remplacement rapide</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types de bris de glace */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-10 text-center">
            Types de bris de glace que nous traitons
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Bris de glace fenêtre",
                desc: "Remplacement vitrage fenêtre cassée suite à choc ou accident",
                icon: "window"
              },
              {
                title: "Bris de glace baie vitrée",
                desc: "Intervention urgente pour baie vitrée endommagée",
                icon: "door"
              },
              {
                title: "Bris de glace vitrine",
                desc: "Sécurisation et remplacement vitrine commerce",
                icon: "store"
              },
              {
                title: "Bris de glace porte vitrée",
                desc: "Remplacement vitrage porte d'entrée ou intérieure",
                icon: "door-open"
              },
              {
                title: "Bris de glace véranda",
                desc: "Réparation vitrage véranda ou vérandière",
                icon: "home"
              },
              {
                title: "Bris de glace miroir",
                desc: "Remplacement miroir cassé ou fêlé",
                icon: "square"
              }
            ].map((item, i) => (
              <Card key={i} className="border-0 shadow-md hover:shadow-lg border-l-4 border-l-[hsl(207,78%,42%)]">
                <CardContent className="p-6">
                  <h3 className="font-bold text-[hsl(215,25%,15%)] mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Notre intervention */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-10 text-center">
            Notre intervention bris de glace à Villiers-sur-Marne
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-md text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full bg-[hsl(24,95%,53%)] text-white flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">1</span>
                </div>
                <h3 className="font-bold text-[hsl(215,25%,15%)] mb-2">Sécurisation</h3>
                <p className="text-muted-foreground text-sm">
                  Sécurisation immédiate de la zone pour éviter les blessures et les intrusions
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-md text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full bg-[hsl(207,78%,42%)] text-white flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">2</span>
                </div>
                <h3 className="font-bold text-[hsl(215,25%,15%)] mb-2">Nettoyage</h3>
                <p className="text-muted-foreground text-sm">
                  Retrait sécurisé des débris de verre et nettoyage complet de la zone
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-md text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full bg-[hsl(120,40%,45%)] text-white flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">3</span>
                </div>
                <h3 className="font-bold text-[hsl(215,25%,15%)] mb-2">Remplacement</h3>
                <p className="text-muted-foreground text-sm">
                  Pose du nouveau vitrage identique ou amélioré selon vos besoins
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contenu SEO */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">
              Intervention bris de glace à Villiers-sur-Marne
            </h2>
            
            <div className="prose prose-lg text-muted-foreground space-y-4">
              <p>
                Un <strong>bris de glace</strong> peut survenir pour de nombreuses raisons : 
                choc accidentel, tentative d'effraction, tempête, ou choc thermique. 
                Quelle que soit la cause, <strong>NGL SERVICES</strong> intervient en urgence 
                à <strong>Villiers-sur-Marne</strong> pour sécuriser votre habitation et remplacer 
                votre vitrage dans les plus brefs délais.
              </p>
              
              <p>
                Notre <strong>vitrier urgence</strong> est disponible <strong>24h/24 et 7j/7</strong> 
                pour répondre à toutes les situations de <strong>bris de glace</strong>. 
                Nous intervenons en moins d'une heure sur Villiers-sur-Marne (94350) 
                et dans tout le Val-de-Marne.
              </p>

              <h3 className="text-xl font-bold text-[hsl(215,25%,15%)] mt-8 mb-4">
                Que faire en cas de bris de glace ?
              </h3>
              
              <ol className="space-y-2">
                <li><strong>1. Sécurisez la zone</strong> : Éloignez enfants et animaux des débris</li>
                <li><strong>2. Ne touchez pas au verre</strong> : Risque de coupures</li>
                <li><strong>3. Appelez un vitrier</strong> : Contactez NGL SERVICES au {companyInfo.phoneEmergency}</li>
                <li><strong>4. Protégez l'ouverture</strong> : En attendant le vitrier, vous pouvez poser un carton</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Liens internes */}
      <section className="py-12 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-6">Voir aussi :</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/vitrier-villiers-sur-marne" className="px-4 py-2 bg-white text-[hsl(207,78%,42%)] rounded-full text-sm shadow hover:shadow-md">
              Vitrier Villiers-sur-Marne
            </Link>
            <Link to="/vitrier-urgence-villiers-sur-marne" className="px-4 py-2 bg-white text-[hsl(207,78%,42%)] rounded-full text-sm shadow hover:shadow-md">
              Vitrier urgence 24h/24
            </Link>
            <Link to="/remplacement-vitre-cassee-villiers-sur-marne" className="px-4 py-2 bg-white text-[hsl(207,78%,42%)] rounded-full text-sm shadow hover:shadow-md">
              Remplacement vitre cassée
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[hsl(24,95%,53%)]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Bris de glace à Villiers-sur-Marne ?
          </h2>
          <p className="text-white/90 mb-8">
            Notre vitrier intervient en urgence 24h/24 pour sécuriser et remplacer votre vitrage.
          </p>
          <a href={`tel:${companyInfo.phoneEmergency}`}>
            <Button size="lg" className="bg-white text-[hsl(24,95%,53%)] hover:bg-gray-100 rounded-full text-2xl px-12 py-8 font-bold">
              <Phone className="w-8 h-8 mr-3" />
              {companyInfo.phoneEmergency}
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default BrisDeGlacePage;
